import { useCallback, useEffect, useState } from 'react';
import { getJournal, addJournalEntry, type StoredJournalEntry } from '../api.js';
import {
  emptyJournalForm,
  toggleRating,
  toggleChip,
  addChips,
  hasContent,
  buildJournalBody,
  groupByDay,
  type JournalFormState,
} from '../lib/journalForm.js';
import { Button, Card, EmptyState, Group, IconButton, MoreRow, Page, Row, Sheet } from '../ui/index.js';

const RATINGS = [1, 2, 3, 4, 5];
const WORDS = ['Awful', 'Low', 'Okay', 'Good', 'Great'];

function dayHeading(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  const now = new Date();
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return 'Today';
  if (d.toDateString() === yest.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

function RatingScale({
  label,
  value,
  onPick,
}: {
  label: string;
  value: number | null;
  onPick: (v: number) => void;
}): JSX.Element {
  return (
    <div className="jr-scale" role="group" aria-label={label}>
      <span className="jr-scale__label">{label}</span>
      <div className="jr-scale__row">
        {RATINGS.map((n) => (
          <button
            key={n}
            type="button"
            className={`jr-scale__btn jr-scale__btn--${n}${value === n ? ' jr-scale__btn--on' : ''}`}
            onClick={() => onPick(n)}
            aria-label={`${label} ${n}`}
            aria-pressed={value === n}
          >
            <span className="jr-scale__n num">{n}</span>
            <span className="jr-scale__w">{WORDS[n - 1]}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ChipEditor({
  label,
  placeholder,
  values,
  onAdd,
  onRemove,
}: {
  label: string;
  placeholder: string;
  values: string[];
  onAdd: (raw: string) => void;
  onRemove: (v: string) => void;
}): JSX.Element {
  const [input, setInput] = useState('');
  function commit(): void {
    if (input.trim().length === 0) return;
    onAdd(input);
    setInput('');
  }
  return (
    <div className="hb-field">
      <span>{label}</span>
      <div className="jr-chips">
        {values.map((v) => (
          <button key={v} type="button" className="ui-chip ui-chip--on" onClick={() => onRemove(v)} aria-label={`Remove ${v}`}>
            {v} <span aria-hidden="true">×</span>
          </button>
        ))}
        <input
          className="jr-chips__input"
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit();
            }
          }}
          onBlur={commit}
        />
      </div>
    </div>
  );
}

function entryTitle(e: StoredJournalEntry): string {
  const bits = [
    e.mood !== null ? `Mood ${e.mood}` : null,
    e.energy !== null ? `Energy ${e.energy}` : null,
  ].filter(Boolean);
  return bits.join(' · ') || (e.symptoms.length > 0 ? e.symptoms.join(', ') : 'Entry');
}

export default function Journal(): JSX.Element {
  const [form, setForm] = useState<JournalFormState>(emptyJournalForm());
  const [entries, setEntries] = useState<StoredJournalEntry[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState(false);
  const [saved, setSaved] = useState(false);
  const [days, setDays] = useState(7);

  const reload = useCallback(() => {
    getJournal()
      .then(setEntries)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load journal.'))
      .finally(() => setLoaded(true));
  }, []);
  useEffect(() => reload(), [reload]);

  function save(): void {
    if (!hasContent(form)) return;
    setBusy(true);
    setError(null);
    addJournalEntry(buildJournalBody(form))
      .then(() => {
        setForm(emptyJournalForm());
        setDetails(false);
        setSaved(true);
        window.setTimeout(() => setSaved(false), 2400);
        reload();
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Save failed.'))
      .finally(() => setBusy(false));
  }

  const groups = groupByDay(entries);
  const shownGroups = groups.slice(0, days);
  const extra = form.symptoms.length + form.tags.length + (form.text.trim() ? 1 : 0);

  return (
    <Page
      title="Journal"
      sub="How are you feeling?"
      className="jr"
      actions={<IconButton icon="pencil" label="Write an entry" onClick={() => setDetails(true)} />}
    >
      {error && (
        <p className="hb-form__error jr-error" role="alert">
          {error}
        </p>
      )}

      <Card eyebrow={saved ? 'Saved' : 'Right now'} className="jr-quick" label="Log mood and energy">
        <RatingScale label="Mood" value={form.mood} onPick={(v) => setForm((f) => ({ ...f, mood: toggleRating(f.mood, v) }))} />
        <RatingScale label="Energy" value={form.energy} onPick={(v) => setForm((f) => ({ ...f, energy: toggleRating(f.energy, v) }))} />
        <div className="jr-quick__actions">
          <Button disabled={busy || !hasContent(form)} onClick={save} icon="check">
            {busy ? 'Saving…' : 'Log it'}
          </Button>
          <Button variant="plain" onClick={() => setDetails(true)}>
            {extra > 0 ? `Details (${extra})` : 'Add details'}
          </Button>
        </div>
      </Card>

      {loaded && groups.length === 0 && (
        <div className="ui-group">
          <EmptyState icon="book" title="No entries yet" hint="Two taps a day is enough — mood and energy add up to a pattern." />
        </div>
      )}

      {shownGroups.map((group) => (
        <Group key={group.date} header={dayHeading(group.date)}>
          {group.entries.map((e) => (
            <Row
              key={e.id}
              className="jr-entry"
              leading={
                <span className={`jr-badge jr-badge--${e.mood ?? 0}`} aria-label={e.mood !== null ? `Mood ${e.mood}` : 'No mood'}>
                  {e.mood ?? '–'}
                </span>
              }
              title={entryTitle(e)}
              sub={
                e.text || e.symptoms.length > 0 || e.tags.length > 0 ? (
                  <>
                    {e.text && <span className="jr-entry__text">{e.text}</span>}
                    {(e.symptoms.length > 0 || e.tags.length > 0) && (
                      <span className="jr-entry__pills">
                        {e.symptoms.map((s) => (
                          <span key={`s-${s}`} className="hb-pill hb-pill--warn">
                            {s}
                          </span>
                        ))}
                        {e.tags.map((t) => (
                          <span key={`t-${t}`} className="hb-pill">
                            {t}
                          </span>
                        ))}
                      </span>
                    )}
                  </>
                ) : undefined
              }
              detail={new Date(e.createdAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
            />
          ))}
        </Group>
      ))}
      {groups.length > days && (
        <ul className="ui-group jr-more">
          <MoreRow count={groups.length - days} noun="earlier days" onClick={() => setDays((d) => d + 14)} />
        </ul>
      )}

      {details && (
        <Sheet
          title="Journal entry"
          onClose={() => setDetails(false)}
          action={{ label: busy ? 'Saving…' : 'Save', onClick: save, disabled: busy || !hasContent(form) }}
        >
          <div className="hb-form">
            <RatingScale label="Mood" value={form.mood} onPick={(v) => setForm((f) => ({ ...f, mood: toggleRating(f.mood, v) }))} />
            <RatingScale label="Energy" value={form.energy} onPick={(v) => setForm((f) => ({ ...f, energy: toggleRating(f.energy, v) }))} />
            <ChipEditor
              label="Symptoms"
              placeholder="Headache, comma for more"
              values={form.symptoms}
              onAdd={(raw) => setForm((f) => ({ ...f, symptoms: addChips(f.symptoms, raw) }))}
              onRemove={(v) => setForm((f) => ({ ...f, symptoms: toggleChip(f.symptoms, v) }))}
            />
            <ChipEditor
              label="Tags"
              placeholder="Work, travel…"
              values={form.tags}
              onAdd={(raw) => setForm((f) => ({ ...f, tags: addChips(f.tags, raw) }))}
              onRemove={(v) => setForm((f) => ({ ...f, tags: toggleChip(f.tags, v) }))}
            />
            <label className="hb-field">
              <span>Note</span>
              <textarea
                placeholder="Anything worth noting?"
                rows={4}
                value={form.text}
                onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
              />
            </label>
          </div>
        </Sheet>
      )}
    </Page>
  );
}
